'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { IFooter } from '@/sanity/lib/definitions';

import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import Logo from '@/components/global/Logo';
import { resolvePageLink } from '@/sanity/globals/CTA';
import { CTAButton } from '@/shadcn/ui/button';
import FooterForm from '@/components/pageComponents/global/FooterForm';
import { useCookieBanner } from 'cookiebanner-finally';

export default function Footer({
  data,
  className,
}: {
  data: IFooter;
  className?: string;
}) {
  const { show } = useCookieBanner();

  return (
    <footer
      className={cn(
        'pt-[8rem] lg:pt-[17.25rem] bg-black border-t-[.125rem] border-[rgba(218,218,218,0.16)]',
        className,
      )}
    >
      <div className="container pb-[10rem] lg:pb-[15rem]">
        <div className="flex items-start flex-wrap lg:gap-0 gap-[7rem] justify-between">
          <div className={cn('flex items-start gap-[3rem] lg:gap-[5rem]', data.hideForm ? 'max-w-none' : 'max-w-[64.75rem]')}>
            <div className="flex lg:flex-row flex-col gap-[3rem] lg:gap-[5rem] items-start flex-shrink-0">
              <LogoSymbol className="w-[5rem] min-w-[5rem] h-auto" />

              <h3 className="font-newsreader uppercase italic font-[300] text-offwhite text-[3rem]">
                ABOUT <br /> VEYLOR
              </h3>
            </div>

            <div className={cn('flex text-offwhite flex-col gap-[4rem] lg:gap-[5rem]', data.hideForm ? 'max-w-[55rem]' : 'max-w-[35rem]')}>
              <PortableText value={data.about} components={richTextComponent} />
              {data.learnMoreButton && (
                <CTAButton data={data?.learnMoreButton} />
              )}
            </div>
          </div>
          <div className="flex items-start gap-[7rem] lg:gap-[12rem] w-fit lg:m-0 m-auto">
            <div className="flex lg:flex-row flex-col items-start gap-[4.62rem] lg:gap-[5rem] justify-between lg:w-fit">
              <h3 className="font-newsreader uppercase italic font-[300] text-offwhite text-[3rem]">
                LEGAL
              </h3>
              <ul className="flex flex-col gap-[1.875rem] ">
                {data.links?.map((item, index) => (
                  <li key={index}>
                    <Link
                      style={{
                        textWrap: 'nowrap',
                      }}
                      className="font-newsreader uppercase italic font-[300] text-offwhite text-[2rem] opacity-70 transition-all duration-300 hover:opacity-100 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[0.0625rem] after:bg-offwhite after:transition-all after:duration-300 hover:after:w-full"
                      href={resolvePageLink(item.link) || '/'}
                      target={item.link?.openInNewTab ? '_blank' : undefined}
                      rel={
                        item.link?.openInNewTab
                          ? 'noopener noreferrer'
                          : undefined
                      }
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex lg:flex-row flex-col items-start gap-[4.62rem] lg:gap-[5rem] justify-between lg:w-fit">
              <h3 className="font-newsreader uppercase italic font-[300] text-offwhite text-[3rem]">
                Social
              </h3>
              <ul className="flex flex-col gap-[1.875rem]">
                {data.socialLinks?.map((item, index) => (
                  <li key={index}>
                    <Link
                      className="font-newsreader uppercase italic font-[300] text-offwhite text-[2rem] opacity-70 transition-all duration-300 hover:opacity-100 relative inline-block after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[0.0625rem] after:bg-offwhite after:transition-all after:duration-300 hover:after:w-full"
                      href={resolvePageLink(item.link) || '/'}
                      target={item.link?.openInNewTab ? '_blank' : undefined}
                      rel={
                        item.link?.openInNewTab
                          ? 'noopener noreferrer'
                          : undefined
                      }
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {!data.hideForm && <FooterForm />}
        </div>
      </div>

      <svg
        className="w-full h-auto"
        xmlns="http://www.w3.org/2000/svg"
        width="1920"
        height="351"
        fill="none"
        viewBox="0 0 1920 351"
      >
        <path
          fill="#0C0C0C"
          d="M1456.27 0c-100.72 0-188.98 84.212-188.98 180.12 0 103.185 77.23 178.04 183.66 178.04s189.5-81.743 189.5-178.56c0-104.095-77.49-179.6-184.18-179.6m-1.04 343.865c-69.05 0-113.7-64.069-113.7-163.226 0-105.784 40.5-166.344 110.97-166.344 70.48 0 113.19 64.329 113.19 163.745 0 106.954-39.2 165.825-110.46 165.825M198.688 30.659c5.841 9.357-2.596 32.489-15.316 67.577l-61.004 164.395L43.323 57.43c-8.047-20.923-11.032-30.15-8.566-33.789.909-1.3 2.596-3.898 21.935-4.418l8.826-.26V7.137H-68.949v12.346l4.802.39c23.234 1.819 24.531 3.508 45.948 58.22L91.477 357.889h10.514l93.712-250.556c29.723-81.093 35.953-84.472 63.988-87.33V7.396h-99.293v12.475l2.077.26c19.989 2.08 32.319 4.549 36.472 11.047zM858.046 30.804c5.191 9.747-6.88 34.309-23.104 65.368l-40.366 75.765-65.936-119.17c-9.605-17.804-9.475-22.873-7.918-25.732 2.077-3.898 9.605-6.238 23.753-7.277h2.466V7.672h-139.01v12.216l4.802.39c20.378 1.819 27.127 7.927 47.635 42.885L734.87 196.37v136.324l-1.947.91c-2.206 1.039-7.787 2.859-19.988 3.898l-16.095 1.43v12.346h144.332v-12.216l-10.643-.91c-6.749-.65-19.34-1.819-24.791-3.119l-2.466-.52v-148.28l46.726-87.07c35.953-68.228 48.024-75.895 69.051-78.235l6.36-.65V7.803h-98.774v12.736l3.245.39c17.133 1.949 25.699 4.938 28.425 10.136zM1109.72 337.997l-42.32-2.47V25.971l1.95-.91c2.6-1.169 8.18-2.728 19.86-3.768l22.06-1.56V7.518H966.942v12.475l7.009.78c5.581.52 17.263 1.69 22.584 2.86l2.596.519v308.776l-1.947.91c-2.336 1.04-7.268 2.469-17.782 3.639l-12.849 1.299v12.476h254.787l18.3-126.967H1229l-4.41 9.487c-29.34 66.537-49.07 104.225-114.74 104.225zM1927.43 289.406l-68.92-106.434 4.28-1.17c45.82-12.086 75.54-44.705 75.54-83.302 0-60.82-47.38-91.62-140.83-91.62l-142.38.52v12.346l10.64.91c7.14.65 18.95 1.82 24.27 3.12l2.47.52v307.736l-1.95.779c-3.37 1.43-10.25 3.379-19.21 4.549l-16.74 1.429v12.346h145.37v-12.216l-11.55-.909c-11.82-1.17-20.25-2.469-24.54-3.769l-2.33-.65V175.564l4.28 1.56c6.88 2.469 13.63 4.548 20.25 6.368l1.3.389.26.52 92.41 156.468 5.58 10.396h103.32v-12.086h-.26c-26.48-2.469-36.21-10.396-61.13-49.643zm-135.25-122.549c-7.92 0-16.09-1.429-28.3-4.678l-2.33-.65V22.216l17.78-.91c29.85-.65 52.57 6.368 67.49 20.923 13.24 12.866 19.86 30.93 19.86 53.672 0 45.745-26.48 70.956-74.63 70.956zM458.924 337.347l-55.163-2.469V168.274l1.558.26c15.316 1.949 27.776 5.718 37.121 14.035 11.812 10.526 16.614 25.081 18.172 30.67h4.412c21.806-38.727 50.231-44.056 62.821-44.965v-9.227c-12.59-.91-41.015-6.238-62.821-44.965h-4.412c-1.558 5.588-6.36 20.143-18.172 30.669-9.345 8.318-21.805 12.086-37.121 14.036l-1.558.26V21.163h45.299c65.806 0 85.924 28.33 106.172 92.009l3.245 10.397 11.032-1.56L562.63 7.518H302.781v12.605l5.451.65c7.139.65 18.821 1.82 24.142 3.119l2.466.52v307.997l-1.947.779c-2.855 1.17-8.696 2.859-21.156 4.289l-9.475 1.169v12.606h270.752l18.301-118.26-11.162-1.56-3.894 10.007c-22.584 56.921-51.139 95.128-117.335 96.038z"
        ></path>
      </svg>

      <div className=" bg-[#0c0c0c] py-[4rem]">
        <div className="container">
          <div className="flex items-center flex-wrap lg:gap-0 gap-[3rem] justify-between">
            <p className="text-offwhite text-[2rem] font-nunito opacity-70 mb-0">
              Veylor® {new Date().getFullYear()}. All rights reserved.
            </p>
            <div className="flex items-center gap-[3rem]">
              {data.links
                ?.filter((item) =>
                  ['privacy', 'terms'].some((key) =>
                    item.title.toLowerCase().includes(key),
                  ),
                )
                .map((item, index) => (
                  <Link
                    key={index}
                    href={resolvePageLink(item.link) || '/'}
                    target={item.link?.openInNewTab ? '_blank' : undefined}
                    rel={
                      item.link?.openInNewTab
                        ? 'noopener noreferrer'
                        : undefined
                    }
                    className="text-offwhite text-[2rem] font-nunito opacity-70 transition-opacity duration-300 hover:opacity-100"
                  >
                    {item.title}
                  </Link>
                ))}
              <button
                onClick={show}
                className="text-offwhite text-[2rem] font-nunito opacity-70 transition-opacity duration-300 hover:opacity-100"
              >
                Cookie Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function LogoSymbol(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="41"
      fill="none"
      viewBox="0 0 40 41"
      {...props}
    >
      <rect width="40" height="40" fill="#fff" fillOpacity="0.04" rx="4"></rect>
      <rect
        width="39"
        height="39"
        x="0.5"
        y="0.5"
        stroke="#DADADA"
        strokeOpacity="0.16"
        rx="3.5"
      ></rect>
      <g filter="url(#filter0_d_6913_9017)">
        <path
          fill="url(#paint0_linear_6913_9017)"
          d="m31 16.59-.136-.139c-.715.183-2.011.43-3.608.386l-.122.117c.309.705.715 2.193-.78 3.145-1.296.953-2.803.86-3.938-.247-1.656-1.617-2.38-4.24-1.436-5.196.394-.414.881-.632 1.258-.746v-.162A10.6 10.6 0 0 1 20.104 11h-.208a10.7 10.7 0 0 1-2.134 2.748v.162c.377.114.864.332 1.258.746.944.957.22 3.579-1.436 5.196-1.13 1.107-2.638 1.204-3.938.247-1.491-.952-1.089-2.44-.78-3.145l-.122-.117a13.4 13.4 0 0 1-3.608-.386L9 16.59c.246.665.58 1.86.525 3.356l.123.118c1.364-.523 2.35-.012 3.176.515 3.74 2.383 6.056 5.143 7.068 8.422h.207c1.013-3.279 3.33-6.04 7.069-8.422.825-.527 1.816-1.038 3.176-.515l.122-.118a8.9 8.9 0 0 1 .525-3.356zm-10.994 9.719c-.953-1.788-2.032-2.963-3.659-4.536 2.059-.474 3.198-2.302 3.66-3.652.46 1.35 1.6 3.178 3.658 3.652-1.626 1.573-2.706 2.752-3.659 4.536"
        ></path>
        <path
          stroke="url(#paint1_linear_6913_9017)"
          strokeOpacity="0.4"
          strokeWidth="0.3"
          d="M20.015 11.15c.35.647 1.007 1.657 2.065 2.654a3 3 0 0 0-1.206.747c-.268.27-.409.65-.45 1.079s.016.915.154 1.416a6.7 6.7 0 0 0 1.733 2.913c.59.576 1.284.893 2.004.939.72.044 1.456-.184 2.128-.678v-.001c.782-.502 1.077-1.151 1.127-1.773.046-.562-.112-1.09-.26-1.453l.006-.007c1.524.036 2.77-.19 3.5-.37l.005.005a9 9 0 0 0-.507 3.26l-.008.01c-1.383-.493-2.401.038-3.22.56-3.713 2.367-6.046 5.114-7.09 8.382-1.046-3.268-3.378-6.015-7.092-8.382-.818-.522-1.832-1.053-3.218-.56l-.01-.01a9 9 0 0 0-.504-3.253l.01-.012c.73.18 1.977.406 3.5.37l.008.007c-.149.364-.304.892-.258 1.453.05.624.346 1.277 1.133 1.78v-.001c.672.492 1.405.72 2.123.674.72-.045 1.412-.364 2-.94a6.7 6.7 0 0 0 1.734-2.913c.138-.501.195-.988.154-1.416-.036-.375-.149-.713-.355-.973l-.095-.106a3 3 0 0 0-1.207-.747 10.9 10.9 0 0 0 2.065-2.654zm-.15 6.923c-.455 1.329-1.568 3.097-3.551 3.554l-.27.063.2.19c1.622 1.57 2.688 2.733 3.63 4.499l.132.248.133-.247c.941-1.763 2.008-2.93 3.63-4.5l.199-.19-.269-.063c-1.984-.457-3.096-2.225-3.55-3.554l-.142-.415z"
        ></path>
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_6913_9017"
          x1="20"
          x2="20"
          y1="11"
          y2="29"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F2F0EF"></stop>
          <stop offset="1" stopColor="#6D6D6D"></stop>
        </linearGradient>
        <linearGradient
          id="paint1_linear_6913_9017"
          x1="20"
          x2="20"
          y1="11"
          y2="29"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fff"></stop>
          <stop offset="1" stopColor="#fff" stopOpacity="0.24"></stop>
        </linearGradient>
        <filter
          id="filter0_d_6913_9017"
          width="38"
          height="34"
          x="1"
          y="7"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          ></feColorMatrix>
          <feOffset dy="4"></feOffset>
          <feGaussianBlur stdDeviation="4"></feGaussianBlur>
          <feComposite in2="hardAlpha" operator="out"></feComposite>
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.72 0"></feColorMatrix>
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_6913_9017"
          ></feBlend>
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_6913_9017"
            result="shape"
          ></feBlend>
        </filter>
      </defs>
    </svg>
  );
}
